import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, ArrowUpRight } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { NAV } from '../data/site';

const Header = () => {
  const { t, lang, toggleLang } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm' : 'bg-white border-b border-slate-100'
      }`}
    >
      <div className="container-wide">
        <div className="flex items-center justify-between h-16 lg:h-[70px] gap-4">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-3 shrink-0" aria-label="Metakaolin.info home">
            <span className="hidden sm:flex flex-col leading-none">
              <span className="text-[15px] font-extrabold tracking-tight text-slate-900">metakaolin<span className="text-blue-700">.info</span></span>
              <span className="text-[9px] uppercase tracking-[0.28em] text-slate-400 font-semibold mt-0.5">by Newchem GmbH</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-0.5">
            {NAV.map((item) => {
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.key}
                  to={item.path}
                  className={`px-3 py-2 rounded-md text-[13px] font-semibold whitespace-nowrap transition-colors ${
                    active
                      ? 'text-blue-700 bg-blue-700/5'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  {t(`nav.${item.key}`)}
                </Link>
              );
            })}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleLang}
              data-testid="lang-toggle"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-200 text-[12px] font-bold text-slate-700 hover:border-blue-300 hover:text-blue-700 transition-colors"
              aria-label="Switch language"
            >
              <Globe className="w-3.5 h-3.5" />
              {lang === 'de' ? 'DE' : 'EN'}
              <span className="text-slate-300">/</span>
              <span className="text-slate-400 font-semibold">{lang === 'de' ? 'EN' : 'DE'}</span>
            </button>

            <Link
              to="/contact"
              className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-blue-700 text-white text-[13px] font-semibold hover:bg-blue-800 transition-colors"
            >
              {t('nav.contact')}
              <ArrowUpRight className="w-4 h-4" />
            </Link>

            <button
              onClick={() => setOpen((o) => !o)}
              className="xl:hidden inline-flex items-center justify-center w-10 h-10 rounded-md text-slate-700 hover:bg-slate-100"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="xl:hidden border-t border-slate-200 bg-white">
          <nav className="container-wide py-3 flex flex-col">
            {NAV.map((item) => {
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.key}
                  to={item.path}
                  className={`px-3 py-3 rounded-md text-[14px] font-semibold transition-colors ${
                    active ? 'text-blue-700 bg-blue-700/5' : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {t(`nav.${item.key}`)}
                </Link>
              );
            })}
            <Link
              to="/contact"
              className="mt-2 px-3 py-3 rounded-md bg-blue-700 text-white text-[14px] font-semibold text-center"
            >
              {t('nav.contact')}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
